import { Link } from 'react-router-dom';
import { TriangleAlert } from 'lucide-react';
import { Callout, formatDatum, formatDatumZeit } from '@elbklar/ui';

import type { RadarStillstand } from '@/types';

/**
 * „Das Radar steht“ — oben auf der Radar-Seite und in der Übersichts-Kachel.
 *
 * Vom 31.08. bis 26.10.2026 lief das Radar nie, und nichts in der Oberfläche
 * sagte es: Die Liste sah gepflegt aus, „Letzte Läufe“ war eingeklappt und
 * leer. Die API rechnet den Stillstand aus der Datenbank
 * (`services/radar_laeufe.py`), also auch dann, wenn der Worker gar nicht
 * läuft. Anton kann den Fehler nicht beheben; der Hinweis sagt ihm, dass er
 * sich gerade nicht auf das Radar verlassen kann und wen er fragt.
 */
export function StillstandHinweise({ stillstand }: { stillstand: RadarStillstand[] | undefined }) {
  if (!stillstand?.length) return null;
  return (
    <div className="space-y-2">
      {stillstand.map((s) => (
        <Callout key={`${s.kind}-${s.reason}`} tone="warning" icon={TriangleAlert} title={titel(s)} boxed>
          <p>{text(s)}</p>
          {s.reason === 'kein_profil' && (
            <Link
              to="/einstellungen?reiter=radar"
              className="mt-1 inline-flex min-h-6 items-center font-medium text-foreground underline underline-offset-2 touch:min-h-11"
            >
              Firmenprofil eintragen
            </Link>
          )}
        </Callout>
      ))}
    </div>
  );
}

const IT = 'Bitte der IT Bescheid geben.';
const EINZELHEITEN = 'Einzelheiten im Radar unter „Letzte Läufe“.';

function titel(s: RadarStillstand): string {
  if (s.kind === 'monitor') return s.reason === 'fehler' ? 'Prüflauf fehlgeschlagen' : 'Radar prüft nicht';
  if (s.reason === 'kein_profil') return 'Firmenprofil fehlt';
  return s.reason === 'fehler' ? 'Suche fehlgeschlagen' : 'Suche bleibt aus';
}

function text(s: RadarStillstand): string {
  if (s.kind === 'monitor') {
    if (s.reason === 'fehler') {
      return (
        `Der letzte Prüflauf ist am ${formatDatumZeit(s.failed_run_at)} fehlgeschlagen. Bis das ` +
        `behoben ist, prüft das Radar keine Quellen und meldet keine Fristen. ${IT} ${EINZELHEITEN}`
      );
    }
    const seit = s.last_success_at
      ? `Das Radar hat seit dem ${formatDatum(s.last_success_at)} nicht mehr geprüft — der wöchentliche Prüflauf bleibt aus.`
      : 'Das Radar ist eingeschaltet, hat aber noch nie geprüft. „Jetzt prüfen“ startet einen Lauf.';
    return `${seit} Fristen und Änderungen werden so nicht gemeldet. ${IT}`;
  }

  if (s.reason === 'kein_profil') {
    return 'Die Suche nach neuen Programmen ist eingeschaltet, aber das Firmenprofil ist leer — ohne Profil sucht sie nicht.';
  }
  if (s.reason === 'fehler') {
    return `Die letzte Suche nach neuen Programmen ist am ${formatDatumZeit(s.failed_run_at)} fehlgeschlagen. ${IT} ${EINZELHEITEN}`;
  }
  return s.last_success_at
    ? `Die monatliche Suche nach neuen Programmen ist seit dem ${formatDatum(s.last_success_at)} ausgeblieben. ${IT}`
    : `Die Suche nach neuen Programmen ist eingeschaltet, ist aber noch nie gelaufen. „Jetzt suchen“ startet sie; sonst läuft sie am 1. des Monats. Bleibt der Hinweis danach stehen: ${IT}`;
}
